/**
 * Фокус-режим экрана набора (ТЗ UX-03).
 *
 * Передаётся контекстом, а не пропсом: экран набора вложен в семь разных
 * экранов режимов, и протаскивать через каждый настройку оформления значит
 * добавить семь параметров, которые ни один из них не читает.
 */

import { createContext, useContext } from 'react';

import type { AppScreen } from '../state/app-state.js';

/** Включён ли фокус-режим. По умолчанию нет: панели на месте. */
export const FocusModeContext = createContext(false);

/** Включён ли фокус-режим на этом экране. */
export function useFocusMode(): boolean {
  return useContext(FocusModeContext);
}

/**
 * Экраны, на которых человек печатает.
 *
 * Только на них фокус-режим имеет смысл: убирать нечего там, где нет текста
 * упражнения, с которым панели соревнуются за внимание.
 */
const TYPING_SCREENS = new Set([
  'diagnostics',
  'practice',
  'control-test',
  'my-mistakes',
  'sentences',
  'fundamentals',
  'symbols',
  'packs',
  'layout-switch',
  'tempo',
  'custom-text',
]);

/**
 * Действует ли фокус-режим на этом экране (ТЗ FR-EN-86).
 *
 * Включённый режим не выключается сам — он отступает. На экране итога,
 * главной и в аккаунте он убирал название, «Назад» и панели, не давая
 * взамен ничего: соревноваться с текстом упражнения там нечему, а уйти
 * становилось нечем. Стоит человеку снова начать печатать — режим
 * возвращается, и просить его об этом второй раз не нужно.
 *
 * «Свой текст» до принесённого материала — форма, а не набор: там читают и
 * вставляют, и панели нужны.
 */
export function focusApplies(screen: AppScreen): boolean {
  if (screen.name === 'custom-text') return screen.plan !== null;
  return TYPING_SCREENS.has(screen.name);
}
